import { API_BASE_URL } from "@/config/api";
import { getAuthToken } from "@/utils/auth";

const authHeader = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface Branding {
  id: string;
  appName: string;
  logoUrl: string;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primary: string;
  secondary: string;
  accent: string;
  bgLight: string;
  bgDark: string;
  textLight: string;
  textDark: string;
  cardLight: string;
  cardDark: string;
  sidebarLight: string;
  sidebarDark: string;
  splashImageUrl: string | null;
  onboardingImage1Url: string | null;
  onboardingImage2Url: string | null;
  onboardingImage3Url: string | null;
  onboardingVariant: OnboardingVariant;
  updatedAt: string;
}

/** Onboarding layouts the customer app can render. Must match the backend DTO. */
export const ONBOARDING_VARIANTS = ["photo", "fullBleed", "minimal"] as const;
export type OnboardingVariant = (typeof ONBOARDING_VARIANTS)[number];

export type LogoVariant = "light" | "dark" | "favicon";

/** Customer-app first-run photos: splash background + the three slides. */
export type OnboardingSlot =
  | "splash"
  | "onboarding-1"
  | "onboarding-2"
  | "onboarding-3";

export type BrandingPatch = Partial<Omit<Branding, "id" | "updatedAt">>;

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export const fetchBranding = async (): Promise<Branding> => {
  const res = await fetch(`${API_BASE_URL}/branding`);
  if (!res.ok) throw new Error(`Failed to load branding (${res.status})`);
  const json = (await res.json()) as ApiEnvelope<{ branding: Branding }>;
  return json.data.branding;
};

const parseError = async (res: Response, fallback: string): Promise<string> => {
  try {
    const body = await res.json();
    return body?.message || body?.error || fallback;
  } catch {
    return fallback;
  }
};

export const updateBranding = async (
  patch: BrandingPatch,
): Promise<Branding> => {
  const res = await fetch(`${API_BASE_URL}/branding`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(patch),
  });
  if (!res.ok)
    throw new Error(
      await parseError(res, `Failed to update branding (${res.status})`),
    );
  const json = (await res.json()) as ApiEnvelope<{ branding: Branding }>;
  return json.data.branding;
};

const uploadImage = async (path: string, file: File): Promise<string> => {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE_URL}/branding/${path}`, {
    method: "POST",
    headers: authHeader(),
    body: form,
  });
  if (!res.ok)
    throw new Error(
      await parseError(res, `Image upload failed (${res.status})`),
    );
  const json = (await res.json()) as ApiEnvelope<{ url: string }>;
  return json.data.url;
};

export const uploadLogo = (
  file: File,
  variant: LogoVariant = "light",
): Promise<string> => uploadImage(`logo?variant=${variant}`, file);

export const uploadOnboardingImage = (
  file: File,
  slot: OnboardingSlot,
): Promise<string> => uploadImage(`onboarding-image?slot=${slot}`, file);
